import axios from 'axios';
import { getAuthHeaders } from './auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = {
  // Users
  getUsers: (role) => axios.get(`${API}/users${role ? `?role=${role}` : ''}`, getAuthHeaders()),
  getPendingUsers: () => axios.get(`${API}/users/pending`, getAuthHeaders()),
  createUser: (data) => axios.post(`${API}/users`, data, getAuthHeaders()),
  deleteUser: (id) => axios.delete(`${API}/users/${id}`, getAuthHeaders()),
  approveUser: (id) => axios.put(`${API}/users/${id}/approve`, {}, getAuthHeaders()),
  rejectUser: (id) => axios.put(`${API}/users/${id}/reject`, {}, getAuthHeaders()),
  getOnlineStatus: () => axios.get(`${API}/users/online-status`, getAuthHeaders()),
  getOnlineCallers: () => axios.get(`${API}/users/online-callers`, getAuthHeaders()),
  getCallerPerformance: (callerId) => axios.get(`${API}/caller-performance/${callerId}`, getAuthHeaders()),

  // Auth
  register: (data) => axios.post(`${API}/auth/register`, data),
  heartbeat: () => axios.post(`${API}/auth/heartbeat`, {}, getAuthHeaders()),
  logout: () => axios.post(`${API}/auth/logout`, {}, getAuthHeaders()),

  // Leads
  getLeads: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/leads${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  getLead: (id) => axios.get(`${API}/leads/${id}`, getAuthHeaders()),
  createLead: (data) => axios.post(`${API}/leads`, data, getAuthHeaders()),
  updateLead: (id, data) => axios.put(`${API}/leads/${id}`, data, getAuthHeaders()),
  deleteLead: (id) => axios.delete(`${API}/leads/${id}`, getAuthHeaders()),
  deleteMultipleLeads: (leadIds) => axios.post(`${API}/leads/delete-multiple`, leadIds, getAuthHeaders()),
  assignLeads: (data) => axios.post(`${API}/leads/assign`, data, getAuthHeaders()),
  previewLeadsArchive: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/leads/archive/preview${q ? `?${q}` : ''}`, getAuthHeaders());
  },
  archiveLeads: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.post(`${API}/leads/archive${q ? `?${q}` : ''}`, {}, getAuthHeaders());
  },
  unarchiveLeads: (leadIds) => axios.post(`${API}/leads/unarchive`, leadIds, getAuthHeaders()),
  uploadConversionScreenshot: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/leads/upload-conversion-screenshot`, formData, {
      ...getAuthHeaders(),
      headers: {
        ...getAuthHeaders().headers,
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  exportLeads: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/leads/export/csv${query ? `?${query}` : ''}`, {
      ...getAuthHeaders(),
      responseType: 'blob'
    });
  },
  // Get unique courses from leads
  getLeadCourses: () => axios.get(`${API}/courses`, getAuthHeaders()),
  // Rename / merge a course value across all active leads
  renameLeadCourse: (oldName, newName) =>
    axios.post(`${API}/leads/rename-course`, { old_name: oldName, new_name: newName }, getAuthHeaders()),
  importLeads: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/leads/import`, formData, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
  },
  importLeadsFromUrl: (url, callerIds, randomize) => 
    axios.post(`${API}/leads/import-from-url`, {
      url,
      caller_ids: callerIds || [],
      randomize
    }, getAuthHeaders()),
  
  // Import job status tracking
  getImportStatus: (jobId) => axios.get(`${API}/leads/import-status/${jobId}`, getAuthHeaders()),
  getImportJobs: (limit = 10) => axios.get(`${API}/leads/import-jobs?limit=${limit}`, getAuthHeaders()),
  
  // Unused Leads Management
  getUnusedLeads: (hours = 24) => axios.get(`${API}/leads/unused?hours=${hours}`, getAuthHeaders()),
  getUnusedLeadsSummary: () => axios.get(`${API}/leads/unused/summary`, getAuthHeaders()),
  reclaimLeads: (leadIds) => axios.post(`${API}/leads/reclaim`, leadIds, getAuthHeaders()),

  // Remarks - FIXED: /api/remarks/{lead_id}
  getRemarks: (leadId) => axios.get(`${API}/remarks/${leadId}`, getAuthHeaders()),
  createRemark: (leadId, text) => axios.post(`${API}/remarks/${leadId}`, { text }, getAuthHeaders()),
  
  // Preset Remarks
  getPresetRemarks: () => axios.get(`${API}/preset-remarks`, getAuthHeaders()),
  createPresetRemark: (text) => axios.post(`${API}/preset-remarks`, { text }, getAuthHeaders()),
  deletePresetRemark: (id) => axios.delete(`${API}/preset-remarks/${id}`, getAuthHeaders()),

  // Reminders
  getReminders: () => axios.get(`${API}/reminders`, getAuthHeaders()),
  getDueReminders: () => axios.get(`${API}/reminders/due`, getAuthHeaders()),
  createReminder: (data) => axios.post(`${API}/reminders`, data, getAuthHeaders()),
  deleteReminder: (id) => axios.delete(`${API}/reminders/${id}`, getAuthHeaders()),
  markReminderComplete: (id) => axios.put(`${API}/reminders/${id}/complete`, {}, getAuthHeaders()),

  // Notifications - FIXED: mark-all-read endpoint
  getNotifications: () => axios.get(`${API}/notifications`, getAuthHeaders()),
  markNotificationRead: (id) => axios.put(`${API}/notifications/${id}/read`, {}, getAuthHeaders()),
  markAllNotificationsRead: () => axios.put(`${API}/notifications/mark-all-read`, {}, getAuthHeaders()),

  // Version Check
  getAppVersion: () => axios.get(`${API}/version`),

  // Stats
  getStats: () => axios.get(`${API}/stats`, getAuthHeaders()),
  getAdminDashboard: () => axios.get(`${API}/stats/admin-dashboard`, getAuthHeaders()),
  getProductWiseSales: (date) => axios.get(`${API}/stats/product-wise-sales${date ? `?date=${date}` : ''}`, getAuthHeaders()),
  getConversionFunnel: (params = {}) => {
    const query = new URLSearchParams();
    if (params.start_date) query.append('start_date', params.start_date);
    if (params.end_date) query.append('end_date', params.end_date);
    if (params.caller_id) query.append('caller_id', params.caller_id);
    return axios.get(`${API}/stats/conversion-funnel${query.toString() ? `?${query.toString()}` : ''}`, getAuthHeaders());
  },

  // ── WhatsApp Marketing ────────────────────────────────────────────────
  getWaSettings: () => axios.get(`${API}/whatsapp-marketing/settings`, getAuthHeaders()),
  updateWaSettings: (data) => axios.put(`${API}/whatsapp-marketing/settings`, data, getAuthHeaders()),
  listWaTemplates: () => axios.get(`${API}/whatsapp-marketing/templates`, getAuthHeaders()),
  addWaTemplate: (data) => axios.post(`${API}/whatsapp-marketing/templates`, data, getAuthHeaders()),
  updateWaTemplate: (id, data) => axios.put(`${API}/whatsapp-marketing/templates/${id}`, data, getAuthHeaders()),
  deleteWaTemplate: (id) => axios.delete(`${API}/whatsapp-marketing/templates/${id}`, getAuthHeaders()),
  syncWaTemplates: () => axios.post(`${API}/whatsapp-marketing/templates/sync`, {}, getAuthHeaders()),
  getWaLeadFilters: () => axios.get(`${API}/whatsapp-marketing/lead-filters`, getAuthHeaders()),
  previewWaRecipients: (data) => axios.post(`${API}/whatsapp-marketing/recipients/preview`, data, getAuthHeaders()),
  uploadWaCsv: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/whatsapp-marketing/recipients/upload-csv`, fd, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    });
  },
  listWaCampaigns: () => axios.get(`${API}/whatsapp-marketing/campaigns`, getAuthHeaders()),
  getWaCampaign: (id) => axios.get(`${API}/whatsapp-marketing/campaigns/${id}`, getAuthHeaders()),
  createWaCampaign: (data) => axios.post(`${API}/whatsapp-marketing/campaigns`, data, getAuthHeaders()),
  updateWaCampaign: (id, data) => axios.put(`${API}/whatsapp-marketing/campaigns/${id}`, data, getAuthHeaders()),
  deleteWaCampaign: (id) => axios.delete(`${API}/whatsapp-marketing/campaigns/${id}`, getAuthHeaders()),
  cancelWaCampaign: (id) => axios.post(`${API}/whatsapp-marketing/campaigns/${id}/cancel`, {}, getAuthHeaders()),
  sendWaCampaignNow: (id) => axios.post(`${API}/whatsapp-marketing/campaigns/${id}/send-now`, {}, getAuthHeaders()),
  listWaCampaignMessages: (id, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/whatsapp-marketing/campaigns/${id}/messages${q ? `?${q}` : ''}`, getAuthHeaders());
  },


  // Google Sheet Sync
  getGoogleSheetSyncs: () => axios.get(`${API}/google-sheet-syncs`, getAuthHeaders()),
  createGoogleSheetSync: (sheetUrl) => axios.post(`${API}/google-sheet-syncs`, { sheet_url: sheetUrl }, getAuthHeaders()),
  syncGoogleSheet: (syncId) => axios.post(`${API}/google-sheet-syncs/${syncId}/sync`, {}, getAuthHeaders()),
  deleteGoogleSheetSync: (syncId) => axios.delete(`${API}/google-sheet-syncs/${syncId}`, getAuthHeaders()),
  toggleGoogleSheetSync: (syncId) => axios.put(`${API}/google-sheet-syncs/${syncId}/toggle`, {}, getAuthHeaders()),

  // Caller Groups - FIXED: add-callers and remove-callers endpoints
  getCallerGroups: () => axios.get(`${API}/caller-groups`, getAuthHeaders()),
  createCallerGroup: (name, description) => axios.post(`${API}/caller-groups`, { name, description }, getAuthHeaders()),
  updateCallerGroup: (id, data) => axios.put(`${API}/caller-groups/${id}`, data, getAuthHeaders()),
  addCallersToGroup: (groupId, callerIds) => axios.post(`${API}/caller-groups/${groupId}/add-callers`, { caller_ids: callerIds }, getAuthHeaders()),
  removeCallersFromGroup: (groupId, callerIds) => axios.post(`${API}/caller-groups/${groupId}/remove-callers`, { caller_ids: callerIds }, getAuthHeaders()),
  deleteCallerGroup: (id) => axios.delete(`${API}/caller-groups/${id}`, getAuthHeaders()),

  // Lead Groups - FIXED: add-leads and remove-leads endpoints
  getLeadGroups: () => axios.get(`${API}/lead-groups`, getAuthHeaders()),
  getLeadGroup: (id) => axios.get(`${API}/lead-groups/${id}`, getAuthHeaders()),
  createLeadGroup: (name, description) => axios.post(`${API}/lead-groups`, { name, description }, getAuthHeaders()),
  updateLeadGroup: (id, data) => axios.put(`${API}/lead-groups/${id}`, data, getAuthHeaders()),
  addLeadsToGroup: (groupId, leadIds) => axios.post(`${API}/lead-groups/${groupId}/add-leads`, { lead_ids: leadIds }, getAuthHeaders()),
  checkDuplicatesInGroup: (groupId, leadIds) => 
    axios.post(`${API}/lead-groups/${groupId}/check-duplicates`, { lead_ids: leadIds }, getAuthHeaders()),
  removeLeadsFromGroup: (groupId, leadIds) => axios.post(`${API}/lead-groups/${groupId}/remove-leads`, { lead_ids: leadIds }, getAuthHeaders()),
  deleteLeadGroup: (id) => axios.delete(`${API}/lead-groups/${id}`, getAuthHeaders()),

  // Group Assignment
  assignLeadGroupToCallerGroup: (leadGroupId, callerGroupId, distributionType) => 
    axios.post(`${API}/groups/assign`, {
      lead_group_id: leadGroupId,
      caller_group_id: callerGroupId,
      distribution_type: distributionType
    }, getAuthHeaders()),

  // Module Access (Admin only)
  updateModuleAccess: (userId, moduleAccess) => 
    axios.put(`${API}/users/${userId}/module-access`, { module_access: moduleAccess }, getAuthHeaders()),

  // User Profile
  getMyProfile: () => axios.get(`${API}/users/me/profile`, getAuthHeaders()),
  updateMyPhone: (phoneNumber) => axios.put(`${API}/users/me/phone`, { phone_number: phoneNumber }, getAuthHeaders()),

  // ============== COMPLAINT MANAGEMENT ==============
  
  // Problem Types
  getProblemTypes: () => axios.get(`${API}/problem-types`, getAuthHeaders()),
  createProblemType: (name) => axios.post(`${API}/problem-types`, { name }, getAuthHeaders()),
  deleteProblemType: (id) => axios.delete(`${API}/problem-types/${id}`, getAuthHeaders()),

  // Complaints
  getComplaints: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/complaints${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  getComplaint: (id) => axios.get(`${API}/complaints/${id}`, getAuthHeaders()),
  getComplaintStats: (date) => axios.get(`${API}/complaints/stats${date ? `?date=${date}` : ''}`, getAuthHeaders()),
  createComplaint: (data) => axios.post(`${API}/complaints`, data, getAuthHeaders()),
  updateComplaint: (id, data) => axios.put(`${API}/complaints/${id}`, data, getAuthHeaders()),
  assignComplaint: (id, solverId) => axios.put(`${API}/complaints/${id}/assign`, { solver_id: solverId }, getAuthHeaders()),
  solveComplaint: (id, solutionRemarks) => axios.put(`${API}/complaints/${id}/solve`, { solution_remarks: solutionRemarks }, getAuthHeaders()),
  escalateComplaint: (id) => axios.put(`${API}/complaints/${id}/escalate`, {}, getAuthHeaders()),
  deleteComplaint: (id) => axios.delete(`${API}/complaints/${id}`, getAuthHeaders()),
  getComplaintSolvers: () => axios.get(`${API}/complaint-solvers`, getAuthHeaders()),

  // Performance (Agent Dashboard)
  getMyPerformance: (module) => axios.get(`${API}/my-performance/${module}`, getAuthHeaders()),

  // Lead History (by phone)
  getLeadHistory: (phone) => axios.get(`${API}/lead-history/${encodeURIComponent(phone)}`, getAuthHeaders()),

  // Products
  getProducts: () => axios.get(`${API}/products`, getAuthHeaders()),
  getAllProducts: () => axios.get(`${API}/products/all`, getAuthHeaders()),
  createProduct: (data) => axios.post(`${API}/products`, data, getAuthHeaders()),
  updateProduct: (id, data) => axios.put(`${API}/products/${id}`, data, getAuthHeaders()),
  deleteProduct: (id) => axios.delete(`${API}/products/${id}`, getAuthHeaders()),

  // Leaderboard
  getLeaderboard: (period) => axios.get(`${API}/leaderboard/${period}`, getAuthHeaders()),
  getLeaderboardByDateRange: (fromDate, toDate) => {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    return axios.get(`${API}/leaderboard?${params.toString()}`, getAuthHeaders());
  },

  // Password Reset
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  getPasswordResetRequests: () => axios.get(`${API}/auth/password-reset-requests`, getAuthHeaders()),
  resetUserPassword: (requestId, newPassword) => 
    axios.put(`${API}/auth/password-reset-requests/${requestId}/reset`, { new_password: newPassword }, getAuthHeaders()),
  rejectPasswordReset: (requestId) => 
    axios.delete(`${API}/auth/password-reset-requests/${requestId}`, getAuthHeaders()),

  // Class Delivery Module
  getClasses: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/classes${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  getClass: (id) => axios.get(`${API}/classes/${id}`, getAuthHeaders()),
  getClassesPendingPdf: () => axios.get(`${API}/classes/pending-pdf`, getAuthHeaders()),
  createClass: (data) => axios.post(`${API}/classes`, data, getAuthHeaders()),
  updateClass: (id, data, classType = 'app') => axios.put(`${API}/classes/${id}?class_type=${classType}`, data, getAuthHeaders()),
  updateClassStatus: (id, data, classType = 'app') => axios.put(`${API}/classes/${id}/status?class_type=${classType}`, data, getAuthHeaders()),
  toggleClassAttribute: (id, attribute) => axios.patch(`${API}/classes/${id}/toggle?attribute=${attribute}`, {}, getAuthHeaders()),
  assignSubstitute: (id, data) => axios.put(`${API}/classes/${id}/substitute`, data, getAuthHeaders()),
  deleteClass: (id, classType = 'app') => axios.delete(`${API}/classes/${id}?class_type=${classType}`, getAuthHeaders()),
  deleteMultipleClasses: (classIds, classType = 'app') => axios.post(`${API}/classes/delete-multiple?class_type=${classType}`, classIds, getAuthHeaders()),
  getClassStats: () => axios.get(`${API}/class-stats`, getAuthHeaders()),
  getTeachers: () => axios.get(`${API}/teachers`, getAuthHeaders()),
  getAllTeachers: () => axios.get(`${API}/teachers/all`, getAuthHeaders()),
  createTeacher: (data) => axios.post(`${API}/teachers`, data, getAuthHeaders()),
  updateTeacher: (id, data) => axios.put(`${API}/teachers/${id}`, data, getAuthHeaders()),
  deleteTeacher: (id) => axios.delete(`${API}/teachers/${id}`, getAuthHeaders()),
  getTeacherPerformance: (teacherId) => axios.get(`${API}/teacher-performance/${teacherId}`, getAuthHeaders()),
  getTeacherCredentials: () => axios.get(`${API}/teacher-credentials`, getAuthHeaders()),
  getTeachersPerformanceReport: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/teachers/performance-report${query ? `?${query}` : ''}`, getAuthHeaders());
  },

  // Subjects - FIXED: /api/subjects
  getSubjects: () => axios.get(`${API}/subjects`, getAuthHeaders()),
  createSubject: (data) => axios.post(`${API}/subjects`, data, getAuthHeaders()),
  updateSubject: (id, data) => axios.put(`${API}/subjects/${id}`, data, getAuthHeaders()),
  deleteSubject: (id) => axios.delete(`${API}/subjects/${id}`, getAuthHeaders()),
  
  // Courses for Class Delivery - FIXED: /api/courses-list (different from lead courses)
  getCoursesList: () => axios.get(`${API}/courses-list`, getAuthHeaders()),
  createCourse: (data) => axios.post(`${API}/courses-list`, data, getAuthHeaders()),
  deleteCourse: (id) => axios.delete(`${API}/courses-list/${id}`, getAuthHeaders()),

  // YouTube Channels
  getYouTubeChannels: () => axios.get(`${API}/youtube-channels`, getAuthHeaders()),
  createYouTubeChannel: (data) => axios.post(`${API}/youtube-channels`, data, getAuthHeaders()),
  updateYouTubeChannel: (id, data) => axios.put(`${API}/youtube-channels/${id}`, data, getAuthHeaders()),
  deleteYouTubeChannel: (id) => axios.delete(`${API}/youtube-channels/${id}`, getAuthHeaders()),

  // Manager creation - FIXED: /api/managers
  createManager: (data) => axios.post(`${API}/managers`, data, getAuthHeaders()),
  
  // Promote agent to manager
  promoteToManager: (userId, moduleAccess) => 
    axios.post(`${API}/users/${userId}/promote-to-manager`, { module_access: moduleAccess }, getAuthHeaders()),
  
  // Also add to users router path as backup
  createManagerViaUsers: (data) => axios.post(`${API}/users/managers`, data, getAuthHeaders()),

  // ============== CONTENT MODULE ==============
  
  // Subjects
  getContentSubjects: () => axios.get(`${API}/content/subjects`, getAuthHeaders()),
  createContentSubject: (data) => axios.post(`${API}/content/subjects`, data, getAuthHeaders()),
  deleteContentSubject: (id) => axios.delete(`${API}/content/subjects/${id}`, getAuthHeaders()),
  
  // Topics
  getContentTopics: (subjectId = null) => axios.get(`${API}/content/topics${subjectId ? `?subject_id=${subjectId}` : ''}`, getAuthHeaders()),
  createContentTopic: (data) => axios.post(`${API}/content/topics`, data, getAuthHeaders()),
  deleteContentTopic: (id) => axios.delete(`${API}/content/topics/${id}`, getAuthHeaders()),
  
  // Questions
  getContentQuestions: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.subject_id) queryParams.append('subject_id', params.subject_id);
    if (params.topic_id) queryParams.append('topic_id', params.topic_id);
    if (params.difficulty) queryParams.append('difficulty', params.difficulty);
    if (params.assigned_to) queryParams.append('assigned_to', params.assigned_to);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    return axios.get(`${API}/content/questions?${queryParams.toString()}`, getAuthHeaders());
  },
  createContentQuestion: (data) => axios.post(`${API}/content/questions`, data, getAuthHeaders()),
  updateContentQuestion: (id, data) => axios.put(`${API}/content/questions/${id}`, data, getAuthHeaders()),
  deleteContentQuestion: (id) => axios.delete(`${API}/content/questions/${id}`, getAuthHeaders()),
  // AI extraction of MCQs from typing PPT
  listTypingPptsForAI: (q = '') =>
    axios.get(`${API}/content/typing-ppts${q ? `?q=${encodeURIComponent(q)}` : ''}`, getAuthHeaders()),
  extractQuestionsFromPpt: (ppt_id, subject_id = null) =>
    axios.post(`${API}/content/questions/extract-from-ppt`,
      { ppt_id, ...(subject_id ? { subject_id } : {}) },
      { ...getAuthHeaders(), timeout: 120000 }),
  approveContentQuestions: (question_ids = [], all_pending = false) =>
    axios.post(`${API}/content/questions/approve`, { question_ids, all_pending }, getAuthHeaders()),
  assignContentQuestions: (questionIds, agentId) => 
    axios.post(`${API}/content/questions/assign`, { question_ids: questionIds, agent_id: agentId }, getAuthHeaders()),
  unassignContentQuestions: (questionIds) => 
    axios.post(`${API}/content/questions/unassign`, questionIds, getAuthHeaders()),
  importContentQuestions: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/content/questions/import`, formData, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
  },
  getContentQuestionStats: () => axios.get(`${API}/content/questions/stats`, getAuthHeaders()),
  
  // Books
  getContentBooks: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.subject_id) queryParams.append('subject_id', params.subject_id);
    if (params.category) queryParams.append('category', params.category);
    if (params.search) queryParams.append('search', params.search);
    return axios.get(`${API}/content/books?${queryParams.toString()}`, getAuthHeaders());
  },
  createContentBook: (formData) => axios.post(`${API}/content/books`, formData, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  }),
  updateContentBook: (id, formData) => axios.put(`${API}/content/books/${id}`, formData, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  }),
  deleteContentBook: (id) => axios.delete(`${API}/content/books/${id}`, getAuthHeaders()),
  uploadBookFile: (bookId, file, format) => {
    const formData = new FormData();
    formData.append('file', file);
    if (format) formData.append('file_format', format);
    return axios.post(`${API}/content/books/${bookId}/files`, formData, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
  },
  // Chunked upload — bypasses the 100 MB ingress cap so books up to 1 GB can be stored.
  uploadBookFileChunked: async (bookId, file, format, onProgress) => {
    const CHUNK_SIZE = 8 * 1024 * 1024; // 8 MB per chunk (comfortably under 100 MB ingress cap)
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

    const initRes = await axios.post(
      `${API}/content/books/${bookId}/files/chunk-init`,
      { filename: file.name, total_size: file.size, total_chunks: totalChunks, file_format: format || null },
      getAuthHeaders(),
    );
    const uploadId = initRes.data.upload_id;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(file.size, start + CHUNK_SIZE);
      const blob = file.slice(start, end);

      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', String(i));
      fd.append('chunk', blob, `chunk_${i}`);

      await axios.post(`${API}/content/books/${bookId}/files/chunk`, fd, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (onProgress) onProgress(Math.round(((i + 1) / totalChunks) * 100));
    }

    return axios.post(
      `${API}/content/books/${bookId}/files/chunk-finalize`,
      { upload_id: uploadId, filename: file.name, file_format: format || null },
      getAuthHeaders(),
    );
  },
  deleteBookFile: (bookId, fileId) => axios.delete(`${API}/content/books/${bookId}/files/${fileId}`, getAuthHeaders()),
  downloadBookFile: (bookId, fileId) => axios.get(`${API}/content/books/${bookId}/files/${fileId}/download`, {
    ...getAuthHeaders(),
    responseType: 'blob'
  }),
  getBookCoverUrl: (bookId) => {
    const token = localStorage.getItem('token');
    return `${API}/content/books/${bookId}/cover?token=${token}`;
  },
  downloadAllBookFiles: (bookId) => axios.get(`${API}/content/books/${bookId}/download-all`, {
    ...getAuthHeaders(),
    responseType: 'blob'
  }),
  
  // Content Agents
  getContentAgents: () => axios.get(`${API}/content/agents`, getAuthHeaders()),

  // ============== STOCK MANAGEMENT ==============
  
  // Stock Products
  getStockProducts: (activeOnly = false) => axios.get(`${API}/stock/products?active_only=${activeOnly}`, getAuthHeaders()),
  createStockProduct: (data) => axios.post(`${API}/stock/products`, data, getAuthHeaders()),
  updateStockProduct: (id, data) => axios.put(`${API}/stock/products/${id}`, data, getAuthHeaders()),
  deleteStockProduct: (id) => axios.delete(`${API}/stock/products/${id}`, getAuthHeaders()),
  bulkImportProducts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/stock/products/bulk-import`, formData, {
      ...getAuthHeaders(),
      headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
    });
  },

  // Stock Offices/Warehouses
  getStockOffices: (activeOnly = true) => axios.get(`${API}/stock/offices?active_only=${activeOnly}`, getAuthHeaders()),
  createStockOffice: (data) => axios.post(`${API}/stock/offices`, data, getAuthHeaders()),
  updateStockOffice: (id, data) => axios.put(`${API}/stock/offices/${id}`, data, getAuthHeaders()),
  deleteStockOffice: (id) => axios.delete(`${API}/stock/offices/${id}`, getAuthHeaders()),

  // Stock Vendors
  getStockVendors: (activeOnly = true) => axios.get(`${API}/stock/vendors?active_only=${activeOnly}`, getAuthHeaders()),
  createStockVendor: (data) => axios.post(`${API}/stock/vendors`, data, getAuthHeaders()),
  updateStockVendor: (id, data) => axios.put(`${API}/stock/vendors/${id}`, data, getAuthHeaders()),
  deleteStockVendor: (id) => axios.delete(`${API}/stock/vendors/${id}`, getAuthHeaders()),

  // Stock Customers
  getStockCustomers: (activeOnly = true) => axios.get(`${API}/stock/customers?active_only=${activeOnly}`, getAuthHeaders()),
  createStockCustomer: (data) => axios.post(`${API}/stock/customers`, data, getAuthHeaders()),
  updateStockCustomer: (id, data) => axios.put(`${API}/stock/customers/${id}`, data, getAuthHeaders()),
  deleteStockCustomer: (id) => axios.delete(`${API}/stock/customers/${id}`, getAuthHeaders()),
  bulkImportCustomers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/stock/customers/bulk-import`, formData, {
      ...getAuthHeaders(),
      headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
    });
  },

  // Stock Transactions
  getStockTransactions: (type = null, limit = 100) => {
    const params = new URLSearchParams();
    if (type) params.append('transaction_type', type);
    params.append('limit', limit);
    return axios.get(`${API}/stock/transactions?${params.toString()}`, getAuthHeaders());
  },
  createStockTransaction: (data) => axios.post(`${API}/stock/transactions`, data, getAuthHeaders()),
  updateStockTransaction: (transactionId, data) => axios.put(`${API}/stock/transactions/${transactionId}`, data, getAuthHeaders()),
  getStockTransaction: (transactionId) => axios.get(`${API}/stock/transactions/${transactionId}`, getAuthHeaders()),
  deleteStockTransaction: (transactionId) => axios.delete(`${API}/stock/transactions/${transactionId}`, getAuthHeaders()),

  // Stock Inventory
  getStockInventory: (officeId = null, productId = null) => {
    const params = new URLSearchParams();
    if (officeId) params.append('office_id', officeId);
    if (productId) params.append('product_id', productId);
    return axios.get(`${API}/stock/inventory?${params.toString()}`, getAuthHeaders());
  },
  getInventoryByProduct: () => axios.get(`${API}/stock/inventory/by-product`, getAuthHeaders()),
  getInventoryByOffice: () => axios.get(`${API}/stock/inventory/by-office`, getAuthHeaders()),
  getInventoryTotals: () => axios.get(`${API}/stock/inventory/totals`, getAuthHeaders()),
  getInventoryByDate: (date) => axios.get(`${API}/stock/inventory/by-date?date=${date}`, getAuthHeaders()),
  adjustInventory: (data) => axios.put(`${API}/stock/inventory/adjust`, data, getAuthHeaders()),

  // Stock Stats
  getStockStats: () => axios.get(`${API}/stock/stats`, getAuthHeaders()),

  // Stock Combos
  getStockCombos: (activeOnly = true) => axios.get(`${API}/stock/combos?active_only=${activeOnly}`, getAuthHeaders()),
  getStockCombo: (id) => axios.get(`${API}/stock/combos/${id}`, getAuthHeaders()),
  createStockCombo: (data) => axios.post(`${API}/stock/combos`, data, getAuthHeaders()),
  updateStockCombo: (id, data) => axios.put(`${API}/stock/combos/${id}`, data, getAuthHeaders()),
  deleteStockCombo: (id) => axios.delete(`${API}/stock/combos/${id}`, getAuthHeaders()),
  createComboTransaction: (data) => axios.post(`${API}/stock/combo-transactions`, data, getAuthHeaders()),

  // Paper Stock (Admin Only)
  getPaperSpecs: () => axios.get(`${API}/stock/paper/specs`, getAuthHeaders()),
  createPaperSpec: (data) => axios.post(`${API}/stock/paper/specs`, data, getAuthHeaders()),
  deletePaperSpec: (id) => axios.delete(`${API}/stock/paper/specs/${id}`, getAuthHeaders()),
  getPaperInward: () => axios.get(`${API}/stock/paper/inward`, getAuthHeaders()),
  createPaperInward: (data) => axios.post(`${API}/stock/paper/inward`, data, getAuthHeaders()),
  getPaperConsumption: () => axios.get(`${API}/stock/paper/consumption`, getAuthHeaders()),
  createPaperConsumption: (data) => axios.post(`${API}/stock/paper/consumption`, data, getAuthHeaders()),
  getPaperStock: () => axios.get(`${API}/stock/paper/stock`, getAuthHeaders()),
  getPaperSummary: () => axios.get(`${API}/stock/paper/summary`, getAuthHeaders()),
  migratePaperWastage: () => axios.post(`${API}/stock/paper/migrate-wastage`, {}, getAuthHeaders()),

  // ============== TYPING MODULE (SIMPLIFIED) ==============
  // Subjects & Teachers (from Class Delivery)
  getTypingSubjects: () => axios.get(`${API}/typing/subjects`, getAuthHeaders()),
  getTypingTeachers: () => axios.get(`${API}/typing/teachers`, getAuthHeaders()),
  
  // Topics Library
  getTypingTopics: (params = {}) => axios.get(`${API}/typing/topics`, { ...getAuthHeaders(), params }),
  createTypingTopic: (data) => axios.post(`${API}/typing/topics`, data, getAuthHeaders()),
  deleteTypingTopic: (id) => axios.delete(`${API}/typing/topics/${id}`, getAuthHeaders()),
  
  // Typing Agents
  getTypingAgents: () => axios.get(`${API}/typing/agents`, getAuthHeaders()),
  
  // PPT Management
  getTypingPPTs: (params = {}) => axios.get(`${API}/typing/ppts`, { ...getAuthHeaders(), params }),
  getTypingPPT: (id) => axios.get(`${API}/typing/ppts/${id}`, getAuthHeaders()),
  uploadTypingPPT: (formData) => axios.post(`${API}/typing/ppts/upload`, formData, {
    ...getAuthHeaders(),
    headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
  }),
  updateTypingPPT: (id, data) => axios.put(`${API}/typing/ppts/${id}`, data, getAuthHeaders()),
  deleteTypingPPT: (id) => axios.delete(`${API}/typing/ppts/${id}`, getAuthHeaders()),
  downloadTypingPPT: (id) => axios.get(`${API}/typing/ppts/${id}/download`, {
    ...getAuthHeaders(),
    responseType: 'blob'
  }),
  verifyTypingPPT: (id) => axios.get(`${API}/typing/ppts/${id}/verify`, getAuthHeaders()),
  reuploadTypingPPT: (id, formData) => axios.post(`${API}/typing/ppts/${id}/reupload`, formData, {
    ...getAuthHeaders(),
    headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
  }),
  checkMyPPTsAvailability: () => axios.get(`${API}/typing/my-ppts/check-availability`, getAuthHeaders()),
  
  // Design Templates Library
  getDesignTemplates: () => axios.get(`${API}/typing/design-templates`, getAuthHeaders()),
  getDesignTemplate: (id) => axios.get(`${API}/typing/design-templates/${id}`, getAuthHeaders()),
  uploadDesignTemplate: (formData) => axios.post(`${API}/typing/design-templates/upload`, formData, {
    ...getAuthHeaders(),
    headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
  }),
  deleteDesignTemplate: (id) => axios.delete(`${API}/typing/design-templates/${id}`, getAuthHeaders()),
  downloadDesignTemplate: (id) => axios.get(`${API}/typing/design-templates/${id}/download`, {
    ...getAuthHeaders(),
    responseType: 'blob'
  }),
  
  // PPT Merger
  mergePPT: (formData) => axios.post(`${API}/typing/merge-ppt`, formData, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    responseType: 'blob'
  }),
  previewMergePPT: (formData) => axios.post(`${API}/typing/merge-ppt/preview`, formData, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  }),
  
  // Stats (for managers)
  getTypingStats: () => axios.get(`${API}/typing/stats`, getAuthHeaders()),
  getTypingDashboardStats: (params) => {
    // Accepts either a legacy YYYY-MM-DD string OR { start_date, end_date }
    let qs = '';
    if (typeof params === 'string' && params) {
      qs = `?date=${params}`;
    } else if (params && typeof params === 'object') {
      const s = params.start_date, e = params.end_date;
      if (s && e) qs = `?start_date=${s}&end_date=${e}`;
      else if (params.date) qs = `?date=${params.date}`;
    }
    return axios.get(`${API}/typing/dashboard-stats${qs}`, getAuthHeaders());
  },
  
  // Teacher-specific
  getMyPPTs: () => axios.get(`${API}/typing/my-ppts`, getAuthHeaders()),

  // ============== HR & FINANCE ==============
  
  // PIN Management (module-specific)
  getHRFinancePinStatus: (module = 'hr') => axios.get(`${API}/hr-finance/pin/status?module=${module}`, getAuthHeaders()),
  verifyHRFinancePin: (pin, module = 'hr') => axios.post(`${API}/hr-finance/pin/verify`, { pin, module }, getAuthHeaders()),

  // HR - Departments
  getDepartments: () => axios.get(`${API}/hr-finance/departments`, getAuthHeaders()),
  createDepartment: (data) => axios.post(`${API}/hr-finance/departments`, data, getAuthHeaders()),
  updateDepartment: (id, data) => axios.put(`${API}/hr-finance/departments/${id}`, data, getAuthHeaders()),
  deleteDepartment: (id) => axios.delete(`${API}/hr-finance/departments/${id}`, getAuthHeaders()),

  // HR - Global Search
  hrGlobalSearch: (query) => axios.get(`${API}/hr-finance/search?q=${encodeURIComponent(query)}`, getAuthHeaders()),

  // HR - Employees
  getHREmployees: () => axios.get(`${API}/hr-finance/employees`, getAuthHeaders()),
  getHREmployee: (id) => axios.get(`${API}/hr-finance/employees/${id}`, getAuthHeaders()),
  updateEmployeeProfile: (employeeId, data) => axios.put(`${API}/hr-finance/employees/${employeeId}/profile`, data, getAuthHeaders()),

  // HR - Documents
  getDocumentTypes: () => axios.get(`${API}/hr-finance/document-types`, getAuthHeaders()),
  getEmployeeDocuments: (employeeId) => axios.get(`${API}/hr-finance/documents/${employeeId}`, getAuthHeaders()),
  addEmployeeDocument: (data) => axios.post(`${API}/hr-finance/documents`, data, getAuthHeaders()),
  deleteEmployeeDocument: (id) => axios.delete(`${API}/hr-finance/documents/${id}`, getAuthHeaders()),

  // HR - Salary
  getSalaryTypes: () => axios.get(`${API}/hr-finance/salary/types`, getAuthHeaders()),
  getSalaryConfig: (employeeId) => axios.get(`${API}/hr-finance/salary/config/${employeeId}`, getAuthHeaders()),
  setSalaryConfig: (data) => axios.post(`${API}/hr-finance/salary/config`, data, getAuthHeaders()),
  calculateSalary: (data) => axios.post(`${API}/hr-finance/salary/calculate`, data, getAuthHeaders()),

  // HR - Attendance
  uploadAttendance: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/hr-finance/attendance/upload`, formData, {
      ...getAuthHeaders(),
      headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
    });
  },
  getEmployeeAttendance: (employeeId) => axios.get(`${API}/hr-finance/attendance/${employeeId}`, getAuthHeaders()),
  getAllAttendance: (month, year) => axios.get(`${API}/hr-finance/attendance`, { 
    ...getAuthHeaders(),
    params: { month, year }
  }),

  // HR/Finance Stats
  getHRFinanceStats: () => axios.get(`${API}/hr-finance/stats`, getAuthHeaders()),

  // Finance - Tally
  getTallyStatus: () => axios.get(`${API}/hr-finance/finance/tally/status`, getAuthHeaders()),
  getFinanceSummary: () => axios.get(`${API}/hr-finance/finance/summary`, getAuthHeaders()),

  // ============== ATTENDANCE MANAGEMENT ==============
  // Team Management
  getAttendanceTeams: () => axios.get(`${API}/attendance/teams`, getAuthHeaders()),
  getAttendanceTeam: (teamId) => axios.get(`${API}/attendance/teams/${teamId}`, getAuthHeaders()),
  createAttendanceTeam: (data) => axios.post(`${API}/attendance/teams`, data, getAuthHeaders()),
  updateTeamMembers: (teamId, memberIds) => axios.put(`${API}/attendance/teams/${teamId}/members`, { member_ids: memberIds }, getAuthHeaders()),
  deleteAttendanceTeam: (teamId) => axios.delete(`${API}/attendance/teams/${teamId}`, getAuthHeaders()),
  
  // Team Leader
  getMyTeam: () => axios.get(`${API}/attendance/my-team`, getAuthHeaders()),
  checkIsTeamLeader: () => axios.get(`${API}/attendance/is-team-leader`, getAuthHeaders()),
  
  // Attendance Marking
  getAttendanceStatuses: () => axios.get(`${API}/attendance/statuses`, getAuthHeaders()),
  markAttendance: (data) => axios.post(`${API}/attendance/mark`, data, getAuthHeaders()),
  markBulkAttendance: (date, attendanceList) => axios.post(`${API}/attendance/mark-bulk`, { date, attendance: attendanceList }, getAuthHeaders()),
  
  // Attendance Data
  getDailyAttendance: (date, teamId = null) => axios.get(`${API}/attendance/daily`, { 
    ...getAuthHeaders(),
    params: { date, team_id: teamId }
  }),
  getEmployeeAttendanceHistory: (employeeId, month = null, year = null) => axios.get(`${API}/attendance/employee/${employeeId}`, {
    ...getAuthHeaders(),
    params: { month, year }
  }),
  
  // Reports
  getUnmarkedAttendance: (date = null) => axios.get(`${API}/attendance/unmarked`, {
    ...getAuthHeaders(),
    params: { date }
  }),
  getMonthlyAttendanceSummary: (month, year) => axios.get(`${API}/attendance/monthly-summary`, {
    ...getAuthHeaders(),
    params: { month, year }
  }),

  // ============== SALES INCENTIVES ==============
  // Admin - Point Kitty Management
  getAllManagerKitties: () => axios.get(`${API}/incentives/kitty/all`, getAuthHeaders()),
  allocatePointsToManager: (managerId, points, remarks) => axios.post(`${API}/incentives/kitty/allocate`, { manager_id: managerId, points, remarks }, getAuthHeaders()),
  getKittyHistory: (managerId) => axios.get(`${API}/incentives/kitty/history/${managerId}`, getAuthHeaders()),

  // Manager - My Kitty
  getMyKitty: () => axios.get(`${API}/incentives/kitty/my`, getAuthHeaders()),

  // Competitions
  getCompetitions: (status = null) => axios.get(`${API}/incentives/competitions`, { ...getAuthHeaders(), params: { status } }),
  getCompetition: (competitionId) => axios.get(`${API}/incentives/competitions/${competitionId}`, getAuthHeaders()),
  createCompetition: (data) => axios.post(`${API}/incentives/competitions`, data, getAuthHeaders()),
  updateCompetition: (competitionId, data) => axios.put(`${API}/incentives/competitions/${competitionId}`, data, getAuthHeaders()),
  completeCompetition: (competitionId) => axios.post(`${API}/incentives/competitions/${competitionId}/complete`, {}, getAuthHeaders()),
  cancelCompetition: (competitionId) => axios.delete(`${API}/incentives/competitions/${competitionId}`, getAuthHeaders()),

  // Caller Points
  getMyPoints: (month = null, year = null) => axios.get(`${API}/incentives/my-points`, { ...getAuthHeaders(), params: { month, year } }),
  getMyYearlyPoints: (year = null) => axios.get(`${API}/incentives/my-points/yearly`, { ...getAuthHeaders(), params: { year } }),
  getCallerPoints: (callerId, month = null, year = null) => axios.get(`${API}/incentives/caller/${callerId}/points`, { ...getAuthHeaders(), params: { month, year } }),
  getAllCallerPoints: (month = null, year = null) => axios.get(`${API}/incentives/all-caller-points`, { ...getAuthHeaders(), params: { month, year } }),

  // Paid in Cash
  markWinnerPaidCash: (winnerId) => axios.post(`${API}/incentives/winners/${winnerId}/paid-cash`, {}, getAuthHeaders()),
  getPaidCashWinners: (month = null, year = null, callerId = null) => axios.get(`${API}/incentives/winners/paid-cash`, { ...getAuthHeaders(), params: { month, year, caller_id: callerId } }),

  // Stats
  getIncentiveStats: () => axios.get(`${API}/incentives/stats`, getAuthHeaders()),

  // Course Planner
  getCoursePlans: () => axios.get(`${API}/course-planner/plans`, getAuthHeaders()),
  getCoursePlan: (planId) => axios.get(`${API}/course-planner/plans/${planId}`, getAuthHeaders()),
  createCoursePlan: (data) => axios.post(`${API}/course-planner/plans`, data, getAuthHeaders()),
  updateCoursePlan: (planId, data) => axios.put(`${API}/course-planner/plans/${planId}`, data, getAuthHeaders()),
  deleteCoursePlan: (planId) => axios.delete(`${API}/course-planner/plans/${planId}`, getAuthHeaders()),
  finalizePlan: (planId) => axios.post(`${API}/course-planner/plans/${planId}/finalize`, {}, getAuthHeaders()),
  addPlanTopic: (planId, data) => axios.post(`${API}/course-planner/plans/${planId}/topics`, data, getAuthHeaders()),
  updatePlanTopic: (planId, topicId, data) => axios.put(`${API}/course-planner/plans/${planId}/topics/${topicId}`, data, getAuthHeaders()),
  updateTopicLectures: (planId, topicId, data) => axios.put(`${API}/course-planner/plans/${planId}/topics/${topicId}/lectures`, data, getAuthHeaders()),
  deletePlanTopic: (planId, topicId) => axios.delete(`${API}/course-planner/plans/${planId}/topics/${topicId}`, getAuthHeaders()),
  addTeacherToPlan: (planId, teacherId) => axios.post(`${API}/course-planner/plans/${planId}/teachers/${teacherId}`, {}, getAuthHeaders()),
  removeTeacherFromPlan: (planId, teacherId) => axios.delete(`${API}/course-planner/plans/${planId}/teachers/${teacherId}`, getAuthHeaders()),
  exportPlanPDF: (planId) => axios.get(`${API}/course-planner/plans/${planId}/export-pdf`, { ...getAuthHeaders(), responseType: 'blob' }),
  
  // Course Planner Reference Data
  getPlannerSubjects: () => axios.get(`${API}/course-planner/subjects`, getAuthHeaders()),
  getPlannerCourses: () => axios.get(`${API}/course-planner/courses`, getAuthHeaders()),
  getPlannerTeachers: () => axios.get(`${API}/course-planner/teachers`, getAuthHeaders()),

  // ============== OFFLINE CENTRE ==============
  
  // Enquiries
  getOfflineEnquiries: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/offline/enquiries${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  createOfflineEnquiry: (data) => axios.post(`${API}/offline/enquiries`, data, getAuthHeaders()),
  updateOfflineEnquiry: (id, data) => axios.put(`${API}/offline/enquiries/${id}`, data, getAuthHeaders()),
  deleteOfflineEnquiry: (id) => axios.delete(`${API}/offline/enquiries/${id}`, getAuthHeaders()),
  
  // Old Fee Collections
  getOldFeeCollections: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/offline/old-fee-collections${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  createOldFeeCollection: (data) => axios.post(`${API}/offline/old-fee-collections`, data, getAuthHeaders()),
  deleteOldFeeCollection: (id) => axios.delete(`${API}/offline/old-fee-collections/${id}`, getAuthHeaders()),

  // Task Manager
  getTodayTasks: () => axios.get(`${API}/tasks/today`, getAuthHeaders()),
  getTaskUsers: () => axios.get(`${API}/tasks/users`, getAuthHeaders()),
  listTasks: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/tasks${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  createTask: (data) => axios.post(`${API}/tasks`, data, getAuthHeaders()),
  updateTask: (id, data) => axios.put(`${API}/tasks/${id}`, data, getAuthHeaders()),
  completeTask: (id) => axios.put(`${API}/tasks/${id}/complete`, {}, getAuthHeaders()),
  reopenTask: (id) => axios.put(`${API}/tasks/${id}/reopen`, {}, getAuthHeaders()),
  deleteTask: (id) => axios.delete(`${API}/tasks/${id}`, getAuthHeaders()),
  convertEnquiryToAdmission: (enquiryId, data) => axios.post(`${API}/offline/enquiries/${enquiryId}/convert`, data, getAuthHeaders()),

  // Batches
  getOfflineBatches: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/offline/batches${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  createOfflineBatch: (data) => axios.post(`${API}/offline/batches`, data, getAuthHeaders()),
  updateOfflineBatch: (id, data) => axios.put(`${API}/offline/batches/${id}`, data, getAuthHeaders()),
  deleteOfflineBatch: (id) => axios.delete(`${API}/offline/batches/${id}`, getAuthHeaders()),

  // Admissions
  getOfflineAdmissions: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/offline/admissions${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  getOfflineAdmission: (id) => axios.get(`${API}/offline/admissions/${id}`, getAuthHeaders()),
  updateOfflineAdmission: (id, data) => axios.put(`${API}/offline/admissions/${id}`, data, getAuthHeaders()),
  addOfflinePayment: (admissionId, data) => axios.post(`${API}/offline/admissions/${admissionId}/payments`, data, getAuthHeaders()),
  addBatchToAdmission: (admissionId, batchId) => axios.post(`${API}/offline/admissions/${admissionId}/add-batch?batch_id=${batchId}`, {}, getAuthHeaders()),

  // Offline Classes
  getOfflineClasses: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/offline/classes${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  createOfflineClass: (data) => axios.post(`${API}/offline/classes`, data, getAuthHeaders()),
  updateOfflineClass: (id, data) => axios.put(`${API}/offline/classes/${id}`, data, getAuthHeaders()),
  updateOfflineClassStatus: (id, status, actualMinutes, reason) => axios.put(
    `${API}/offline/classes/${id}/status?status=${status}${actualMinutes ? `&actual_duration_minutes=${actualMinutes}` : ''}${reason ? `&cancellation_reason=${encodeURIComponent(reason)}` : ''}`,
    {},
    getAuthHeaders()
  ),
  deleteOfflineClass: (id) => axios.delete(`${API}/offline/classes/${id}`, getAuthHeaders()),

  // Dashboards & Reports
  getOfflineCounsellorDashboard: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/offline/dashboard/counsellor${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  getOfflineManagerDashboard: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/offline/dashboard/manager${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  getOfflineAdmissionReport: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/offline/reports/admissions${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  getOfflineFeeCollectionReport: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/offline/reports/fee-collection${query ? `?${query}` : ''}`, getAuthHeaders());
  },

  // Special Events
  getSpecialEvents: (query) => axios.get(`${API}/special-events${query ? `?${query}` : ''}`, getAuthHeaders()),
  getSpecialEvent: (id) => axios.get(`${API}/special-events/${id}`, getAuthHeaders()),
  createSpecialEvent: (data) => axios.post(`${API}/special-events`, data, getAuthHeaders()),
  updateSpecialEvent: (id, data) => axios.put(`${API}/special-events/${id}`, data, getAuthHeaders()),
  deleteSpecialEvent: (id) => axios.delete(`${API}/special-events/${id}`, getAuthHeaders()),
  uploadSpecialEventThumbnail: (id, formData) => axios.post(`${API}/special-events/${id}/thumbnail`, formData, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } }),
  uploadSpecialEventPPT: (id, formData) => axios.post(`${API}/special-events/${id}/ppt`, formData, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } }),
  submitSpecialEventForReview: (id) => axios.post(`${API}/special-events/${id}/submit-review`, {}, getAuthHeaders()),
  reviewSpecialEvent: (id, data) => axios.post(`${API}/special-events/${id}/review`, data, getAuthHeaders()),
  completeSpecialEvent: (id) => axios.post(`${API}/special-events/${id}/complete`, {}, getAuthHeaders()),
  reopenSpecialEvent: (id) => axios.post(`${API}/special-events/${id}/reopen`, {}, getAuthHeaders()),
  getSpecialEventStats: () => axios.get(`${API}/special-events/stats`, getAuthHeaders()),
  getSpecialEventDashboard: () => axios.get(`${API}/special-events/dashboard`, getAuthHeaders()),
  getSpecialEventTeacherStats: () => axios.get(`${API}/special-events/teacher-stats`, getAuthHeaders()),
  getSpecialEventTeachers: () => axios.get(`${API}/special-events/teachers/list`, getAuthHeaders()),
  getSpecialEventChannels: () => axios.get(`${API}/special-events/channels/list`, getAuthHeaders()),
  getMySpecialEvents: () => axios.get(`${API}/special-events/my-events`, getAuthHeaders()),
  updateYouTubeChannelNotificationConfig: (channelId, payload) => axios.put(`${API}/youtube-channels/${channelId}/notification-config`, payload, getAuthHeaders()),
  getSlotAssignments: (date) => axios.get(`${API}/special-events/slot-assignments?date=${date}`, getAuthHeaders()),
  assignSlot: (payload) => axios.post(`${API}/special-events/slot-assignments`, payload, getAuthHeaders()),
  unassignSlot: (channelId, date, slotIndex) => axios.delete(`${API}/special-events/slot-assignments/${channelId}/${date}/${slotIndex}`, getAuthHeaders()),

  // ─── Marketing ─────────────────────────────────────────────────────────────
  getMarketingCampaigns: (query = '') => axios.get(`${API}/marketing/campaigns${query ? `?${query}` : ''}`, getAuthHeaders()),
  createMarketingCampaign: (payload) => axios.post(`${API}/marketing/campaigns`, payload, getAuthHeaders()),
  getMarketingCampaign: (id) => axios.get(`${API}/marketing/campaigns/${id}`, getAuthHeaders()),
  updateMarketingCampaign: (id, payload) => axios.put(`${API}/marketing/campaigns/${id}`, payload, getAuthHeaders()),
  deleteMarketingCampaign: (id) => axios.delete(`${API}/marketing/campaigns/${id}`, getAuthHeaders()),
  getMarketingRuns: (campaignId) => axios.get(`${API}/marketing/campaigns/${campaignId}/runs`, getAuthHeaders()),
  submitMarketingRun: (campaignId, formData) => axios.post(`${API}/marketing/campaigns/${campaignId}/runs`, formData, {
    headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
  }),
  getMarketingDashboard: () => axios.get(`${API}/marketing/dashboard`, getAuthHeaders()),

  // Cash Deposits
  markCashDeposited: (date, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/offline/cash-deposits/${date}`, formData, {
      ...getAuthHeaders(),
      headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
    });
  },
  unmarkCashDeposited: (date) => axios.delete(`${API}/offline/cash-deposits/${date}`, getAuthHeaders()),
  issueRefund: (admissionId, data) => axios.post(`${API}/offline/admissions/${admissionId}/refund`, data, getAuthHeaders()),
  deletePayment: (admissionId, paymentId) => axios.delete(`${API}/offline/admissions/${admissionId}/payments/${paymentId}`, getAuthHeaders()),

  // Results Module
  getResultExams: () => axios.get(`${API}/results/exams`, getAuthHeaders()),
  createResultExam: (data) => axios.post(`${API}/results/exams`, data, getAuthHeaders()),
  deleteResultExam: (id) => axios.delete(`${API}/results/exams/${id}`, getAuthHeaders()),
  getResults: (params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/results${query ? `?${query}` : ''}`, getAuthHeaders());
  },
  createResult: (formData) => axios.post(`${API}/results`, formData, {
    ...getAuthHeaders(),
    headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
  }),
  updateResult: (id, formData) => axios.put(`${API}/results/${id}`, formData, {
    ...getAuthHeaders(),
    headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
  }),
  deleteResult: (id) => axios.delete(`${API}/results/${id}`, getAuthHeaders()),
  getResultsStats: () => axios.get(`${API}/results/stats`, getAuthHeaders()),
  updateResultStage: (id, data) => axios.put(`${API}/results/${id}/stage`, data, getAuthHeaders()),

  // Result Forms (admin/manager)
  getResultForms: () => axios.get(`${API}/results/forms`, getAuthHeaders()),
  createResultForm: (data) => axios.post(`${API}/results/forms`, data, getAuthHeaders()),
  updateResultForm: (id, data) => axios.put(`${API}/results/forms/${id}`, data, getAuthHeaders()),
  deleteResultForm: (id) => axios.delete(`${API}/results/forms/${id}`, getAuthHeaders()),
  exportResultFormSubmissions: (id, includeFiles = false) =>
    axios.get(
      `${API}/results/forms/${id}/export${includeFiles ? '?include_files=true' : ''}`,
      { ...getAuthHeaders(), responseType: 'blob' }
    ),
  uploadResultFormPoster: (id, formData) => axios.post(
    `${API}/results/forms/${id}/poster`, formData,
    { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } }
  ),
  // Public (no auth)
  getPublicResultForm: (slug) => axios.get(`${API}/results/public/forms/${slug}`),
  submitPublicResultForm: (slug, formData) => axios.post(`${API}/results/public/forms/${slug}/submit`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Course Progress
  getCourseProgressPlans: () => axios.get(`${API}/course-progress/plans`, getAuthHeaders()),
  createCourseProgressPlan: (data) => axios.post(`${API}/course-progress/plans`, data, getAuthHeaders()),
  getCourseProgressPlan: (id) => axios.get(`${API}/course-progress/plans/${id}`, getAuthHeaders()),
  updateCourseProgressPlan: (id, data) => axios.put(`${API}/course-progress/plans/${id}`, data, getAuthHeaders()),
  deleteCourseProgressPlan: (id) => axios.delete(`${API}/course-progress/plans/${id}`, getAuthHeaders()),
  addTopicToPlan: (planId, data) => axios.post(`${API}/course-progress/plans/${planId}/topics`, data, getAuthHeaders()),
  removeTopicFromPlan: (planId, topicId) => axios.delete(`${API}/course-progress/plans/${planId}/topics/${topicId}`, getAuthHeaders()),
  getCourseProgressReport: (courseId) => axios.get(`${API}/course-progress/report${courseId ? `?course_id=${courseId}` : ''}`, getAuthHeaders()),
  getSinglePlanProgress: (planId) => axios.get(`${API}/course-progress/report/${planId}`, getAuthHeaders()),
  getMyProgress: () => axios.get(`${API}/course-progress/my-progress`, getAuthHeaders()),
  exportTeacherClasses: (teacherId, params) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API}/teachers/${teacherId}/classes-export${query ? `?${query}` : ''}`, getAuthHeaders());
  },

  // E-Commerce (Exampur Store)
  getStoreDashboard: () => axios.get(`${API}/ecommerce/dashboard`, getAuthHeaders()),
  getStoreOrders: (status) => axios.get(`${API}/ecommerce/orders${status ? `?status=${status}` : ''}`, getAuthHeaders()),
  getStoreProducts: () => axios.get(`${API}/ecommerce/products`, getAuthHeaders()),
  getStoreCategories: () => axios.get(`${API}/ecommerce/categories`, getAuthHeaders()),
  uploadClassThumbnail: (classId, formData, classType = 'youtube') => axios.post(
    `${API}/classes/${classId}/thumbnail?class_type=${classType}`, formData,
    { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } }
  ),

  // Lead Forms
  getLeadForms: () => axios.get(`${API}/lead-forms`, getAuthHeaders()),
  createLeadForm: (data) => axios.post(`${API}/lead-forms`, data, getAuthHeaders()),
  updateLeadForm: (id, data) => axios.put(`${API}/lead-forms/${id}`, data, getAuthHeaders()),
  deleteLeadForm: (id) => axios.delete(`${API}/lead-forms/${id}`, getAuthHeaders()),
  uploadLeadFormPoster: (id, formData) => axios.post(
    `${API}/lead-forms/${id}/poster`, formData,
    { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } }
  ),
  getLeadFormSubmissions: (id) => axios.get(`${API}/lead-forms/${id}/submissions`, getAuthHeaders()),
  // Public (no auth) — lead form
  getPublicLeadForm: (slug) => axios.get(`${API}/lead-forms/public/${slug}`),
  submitPublicLeadForm: (slug, data) => axios.post(`${API}/lead-forms/public/${slug}/submit`, data),

  // Public (no auth) — complaints
  submitPublicComplaint: (data) => axios.post(`${API}/complaints/public/submit`, data),
  getPublicComplaintStatus: (ticket) => axios.get(`${API}/complaints/public/status/${ticket}`),
  getPublicComplaintStatusByPhone: (phone) => axios.get(`${API}/complaints/public/status-by-phone/${encodeURIComponent(phone)}`),
  getPublicComplaintConfig: () => axios.get(`${API}/complaints/public/config`),

  // Complaint Form Config (admin/manager)
  getComplaintFormConfig: () => axios.get(`${API}/complaint-form-config`, getAuthHeaders()),
  updateComplaintFormConfig: (data) => axios.put(`${API}/complaint-form-config`, data, getAuthHeaders()),
  uploadComplaintFormLogo: (formData) => axios.post(
    `${API}/complaint-form-config/logo`, formData,
    { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } }
  ),

  // ============== DOUBT ENGINE ==============
  // Admin / Manager / Teacher (staff-side)
  getDoubtBatches: () => axios.get(`${API}/doubt-engine/batches`, getAuthHeaders()),
  createDoubtBatch: (data) => axios.post(`${API}/doubt-engine/batches`, data, getAuthHeaders()),
  updateDoubtBatch: (id, data) => axios.put(`${API}/doubt-engine/batches/${id}`, data, getAuthHeaders()),
  deleteDoubtBatch: (id) => axios.delete(`${API}/doubt-engine/batches/${id}`, getAuthHeaders()),
  getDoubtStats: () => axios.get(`${API}/doubt-engine/stats`, getAuthHeaders()),
  getDoubtChats: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/doubt-engine/chats${q ? `?${q}` : ''}`, getAuthHeaders());
  },
  getDoubtChatMessages: (chatId) =>
    axios.get(`${API}/doubt-engine/chats/${chatId}/messages`, getAuthHeaders()),
  sendDoubtStaffText: (chatId, content) =>
    axios.post(`${API}/doubt-engine/chats/${chatId}/messages`, { content }, getAuthHeaders()),
  sendDoubtStaffMedia: (chatId, file, mediaType) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('media_type', mediaType);
    return axios.post(`${API}/doubt-engine/chats/${chatId}/media`, fd, {
      ...getAuthHeaders(),
      headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' },
    });
  },

  // Public (student-side) — no auth headers
  getPublicDoubtBatch: (slug) => axios.get(`${API}/doubt-engine/public/batch/${slug}`),
  createDoubtStudentSession: (data) =>
    axios.post(`${API}/doubt-engine/public/session`, data),
  startPublicDoubtChat: (token, slug, teacherId) =>
    axios.post(`${API}/doubt-engine/public/chats/start?slug=${encodeURIComponent(slug)}&teacher_id=${encodeURIComponent(teacherId)}&token=${encodeURIComponent(token)}`),
  listPublicDoubtChats: (token) =>
    axios.get(`${API}/doubt-engine/public/chats?token=${encodeURIComponent(token)}`),
  getPublicDoubtMessages: (token, chatId) =>
    axios.get(`${API}/doubt-engine/public/chats/${chatId}/messages?token=${encodeURIComponent(token)}`),
  sendPublicDoubtText: (token, chatId, content) =>
    axios.post(
      `${API}/doubt-engine/public/chats/${chatId}/messages?token=${encodeURIComponent(token)}`,
      { content }
    ),
  sendPublicDoubtMedia: (token, chatId, file, mediaType) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('media_type', mediaType);
    return axios.post(
      `${API}/doubt-engine/public/chats/${chatId}/media?token=${encodeURIComponent(token)}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  closePublicDoubtChat: (token, chatId) =>
    axios.post(
      `${API}/doubt-engine/public/chats/${chatId}/close?token=${encodeURIComponent(token)}`
    ),

  // ============== TEACHER FEEDBACK ==============
  getFeedbackForms: () => axios.get(`${API}/teacher-feedback/forms`, getAuthHeaders()),
  createFeedbackForm: (data) => axios.post(`${API}/teacher-feedback/forms`, data, getAuthHeaders()),
  updateFeedbackForm: (id, data) => axios.put(`${API}/teacher-feedback/forms/${id}`, data, getAuthHeaders()),
  deleteFeedbackForm: (id) => axios.delete(`${API}/teacher-feedback/forms/${id}`, getAuthHeaders()),
  getFeedbackAnalytics: (formId = null) =>
    axios.get(`${API}/teacher-feedback/analytics${formId ? `?form_id=${formId}` : ''}`, getAuthHeaders()),
  getFeedbackTeacherDeepDive: (teacherId) =>
    axios.get(`${API}/teacher-feedback/analytics/teacher/${teacherId}`, getAuthHeaders()),
  getFeedbackSubmissions: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/teacher-feedback/submissions${q ? `?${q}` : ''}`, getAuthHeaders());
  },
  getMyFeedback: () => axios.get(`${API}/teacher-feedback/my-feedback`, getAuthHeaders()),
  // Public
  getPublicFeedbackForm: (slug) => axios.get(`${API}/teacher-feedback/public/${slug}`),
  submitPublicFeedback: (slug, data) =>
    axios.post(`${API}/teacher-feedback/public/${slug}/submit`, data),

  // ============== COMPLAINT CHAT ==============
  // Public (student)
  startComplaintChat: (data) => axios.post(`${API}/complaint-chat/start`, data),
  getComplaintChatStatus: (chatId, token) =>
    axios.get(`${API}/complaint-chat/public/status/${chatId}?token=${encodeURIComponent(token)}`),
  getPublicComplaintChatMessages: (chatId, token) =>
    axios.get(`${API}/complaint-chat/public/messages/${chatId}?token=${encodeURIComponent(token)}`),
  sendPublicComplaintChatText: (chatId, token, content) =>
    axios.post(`${API}/complaint-chat/public/messages/${chatId}?token=${encodeURIComponent(token)}`, { content }),
  sendPublicComplaintChatMedia: (chatId, token, file, mediaType = 'image') => {
    const fd = new FormData(); fd.append('file', file); fd.append('media_type', mediaType);
    return axios.post(`${API}/complaint-chat/public/media/${chatId}?token=${encodeURIComponent(token)}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Agent
  getComplaintAgentStatus: () => axios.get(`${API}/complaint-chat/agent/status`, getAuthHeaders()),
  setComplaintAgentStatus: (data) => axios.post(`${API}/complaint-chat/agent/status`, data, getAuthHeaders()),
  complaintAgentHeartbeat: () => axios.post(`${API}/complaint-chat/agent/heartbeat`, {}, getAuthHeaders()),
  getComplaintAgentChats: (include_closed = false) =>
    axios.get(`${API}/complaint-chat/agent/chats?include_closed=${include_closed}`, getAuthHeaders()),
  getComplaintChatMessages: (chatId) =>
    axios.get(`${API}/complaint-chat/chats/${chatId}/messages`, getAuthHeaders()),
  sendComplaintChatText: (chatId, content) =>
    axios.post(`${API}/complaint-chat/chats/${chatId}/messages`, { content }, getAuthHeaders()),
  sendComplaintChatMedia: (chatId, file, mediaType = 'image') => {
    const fd = new FormData(); fd.append('file', file); fd.append('media_type', mediaType);
    return axios.post(`${API}/complaint-chat/chats/${chatId}/media`, fd,
      { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
  },
  closeComplaintChat: (chatId, solution_remarks) =>
    axios.post(`${API}/complaint-chat/chats/${chatId}/close`, { solution_remarks }, getAuthHeaders()),
  registerComplaintTicket: (chatId, solution_remarks) =>
    axios.post(`${API}/complaint-chat/chats/${chatId}/register`, { solution_remarks }, getAuthHeaders()),
  // Admin
  getComplaintChatStats: () => axios.get(`${API}/complaint-chat/stats`, getAuthHeaders()),
  getAllComplaintChats: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/complaint-chat/chats${q ? `?${q}` : ''}`, getAuthHeaders());
  },
  // Presets
  getComplaintPresets: () => axios.get(`${API}/complaint-chat/presets`, getAuthHeaders()),
  createComplaintPreset: (data) => axios.post(`${API}/complaint-chat/presets`, data, getAuthHeaders()),
  updateComplaintPreset: (id, data) => axios.put(`${API}/complaint-chat/presets/${id}`, data, getAuthHeaders()),
  deleteComplaintPreset: (id) => axios.delete(`${API}/complaint-chat/presets/${id}`, getAuthHeaders()),

  // ─── Cash Flow ────────────────────────────────────────────────────────────
  cashflowListCategories: () => axios.get(`${API}/cashflow/categories`, getAuthHeaders()),
  cashflowCreateCategory: (data) => axios.post(`${API}/cashflow/categories`, data, getAuthHeaders()),
  cashflowUpdateCategory: (id, data) => axios.put(`${API}/cashflow/categories/${id}`, data, getAuthHeaders()),
  cashflowDeleteCategory: (id) => axios.delete(`${API}/cashflow/categories/${id}`, getAuthHeaders()),
  cashflowListManagers: () => axios.get(`${API}/cashflow/managers`, getAuthHeaders()),
  cashflowGetMyLimit: () => axios.get(`${API}/cashflow/limits/me`, getAuthHeaders()),
  cashflowUpsertLimit: (managerId, data) => axios.put(`${API}/cashflow/limits/${managerId}`, data, getAuthHeaders()),
  cashflowListEntries: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/cashflow/entries${q ? `?${q}` : ''}`, getAuthHeaders());
  },
  cashflowSummary: (params) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/cashflow/summary?${q}`, getAuthHeaders());
  },
  cashflowCreateEntry: (data) => axios.post(`${API}/cashflow/entries`, data, getAuthHeaders()),
  cashflowUpdateEntry: (id, data) => axios.put(`${API}/cashflow/entries/${id}`, data, getAuthHeaders()),
  cashflowToggleDone: (id, paidOn) => axios.post(`${API}/cashflow/entries/${id}/toggle-done`,
    paidOn ? { paid_on: paidOn } : {}, getAuthHeaders()),
  cashflowBulkAction: (data) => axios.post(`${API}/cashflow/entries/bulk-action`, data, getAuthHeaders()),
  cashflowDeleteEntry: (id, cascadeFuture = false) =>
    axios.delete(`${API}/cashflow/entries/${id}?cascade_future=${cascadeFuture}`, getAuthHeaders()),
  cashflowSampleXlsx: () =>
    axios.get(`${API}/cashflow/entries/sample-xlsx`, { ...getAuthHeaders(), responseType: 'blob' }),
  cashflowImportXlsx: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/cashflow/entries/import`, fd, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
  },
  // Limit extensions
  cashflowExtendLimit: (data) => axios.post(`${API}/cashflow/limits/extend`, data, getAuthHeaders()),
  cashflowListExtensions: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/cashflow/limits/extensions${q ? `?${q}` : ''}`, getAuthHeaders());
  },
  cashflowDeleteExtension: (id) => axios.delete(`${API}/cashflow/limits/extensions/${id}`, getAuthHeaders()),
  // Vendors
  cashflowListVendors: (search) =>
    axios.get(`${API}/cashflow/vendors${search ? `?search=${encodeURIComponent(search)}` : ''}`, getAuthHeaders()),
  cashflowCreateVendor: (data) => axios.post(`${API}/cashflow/vendors`, data, getAuthHeaders()),
  cashflowUpdateVendor: (id, data) => axios.put(`${API}/cashflow/vendors/${id}`, data, getAuthHeaders()),
  cashflowDeleteVendor: (id) => axios.delete(`${API}/cashflow/vendors/${id}`, getAuthHeaders()),
  // Reimbursements
  cashflowListReimbursements: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/cashflow/reimbursements${q ? `?${q}` : ''}`, getAuthHeaders());
  },
  cashflowCreateReimbursement: (data) => axios.post(`${API}/cashflow/reimbursements`, data, getAuthHeaders()),
  cashflowUpdateReimbursement: (id, data) => axios.put(`${API}/cashflow/reimbursements/${id}`, data, getAuthHeaders()),
  cashflowDeleteReimbursement: (id) => axios.delete(`${API}/cashflow/reimbursements/${id}`, getAuthHeaders()),
  cashflowMarkReimbursementPaid: (id) =>
    axios.post(`${API}/cashflow/reimbursements/${id}/mark-paid`, {}, getAuthHeaders()),
  cashflowUnmarkReimbursementPaid: (id) =>
    axios.post(`${API}/cashflow/reimbursements/${id}/unmark-paid`, {}, getAuthHeaders()),
  cashflowUploadInvoice: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/cashflow/reimbursements/${id}/upload-invoice`, fd, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
  },
  // Salaries
  cashflowSalarySampleXlsx: () =>
    axios.get(`${API}/cashflow/salaries/sample-xlsx`, { ...getAuthHeaders(), responseType: 'blob' }),
  cashflowImportSalaries: (file, month) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/cashflow/salaries/import?month=${month}`, fd, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
  },
  cashflowListSalaries: (params) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/cashflow/salaries?${q}`, getAuthHeaders());
  },
  cashflowSalarySummary: (month) =>
    axios.get(`${API}/cashflow/salaries/summary?month=${month}`, getAuthHeaders()),
  cashflowSalaryMonths: () =>
    axios.get(`${API}/cashflow/salaries/months`, getAuthHeaders()),
  cashflowBulkSalaryPay: (data) =>
    axios.post(`${API}/cashflow/salaries/bulk-to-cashflow`, data, getAuthHeaders()),
  // Salary entries (bulk in cashflow calendar, not the retired salaries submodule)
  cashflowSalaryExpenseSampleXlsx: () =>
    axios.get(`${API}/cashflow/entries/salary-sample-xlsx`, { ...getAuthHeaders(), responseType: 'blob' }),
  cashflowSalaryExpenseImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/cashflow/entries/salary-import`, fd, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
  },
  // Finance Controller + consolidated view
  cashflowSetFinanceController: (managerId, isFC) =>
    axios.put(`${API}/cashflow/managers/${managerId}/finance-controller`,
      { is_finance_controller: !!isFC }, getAuthHeaders()),
  cashflowConsolidated: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/cashflow/consolidated${q ? `?${q}` : ''}`, getAuthHeaders());
  },
  cashflowConsolidatedExport: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/cashflow/consolidated/export.xlsx${q ? `?${q}` : ''}`,
      { ...getAuthHeaders(), responseType: 'blob' });
  },
  // Audit log (admin)
  cashflowAudit: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/cashflow/audit${q ? `?${q}` : ''}`, getAuthHeaders());
  },
  cashflowAuditExport: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/cashflow/audit/export.xlsx${q ? `?${q}` : ''}`,
      { ...getAuthHeaders(), responseType: 'blob' });
  },
};
